import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Grid2 as Grid,
	Typography,
	Box,
	useMediaQuery,
	useTheme,
	IconButton,
	InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarIcon } from "@mui/x-date-pickers/icons";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enAU } from "date-fns/locale";
import parseISO from "date-fns/parseISO";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StatusLabel from "../StatusLabel/StatusLabel";
import ReceiveFailureAlert from "../ReceiveFailureAlert/ReceiveFailureAlert";
import { useStyles } from "./Invoice.styles";
import { InvoiceHeaderDetailsProps } from "./types";
import { CopyToClipboard } from "../CopyToClipboard/CopyToClipboard";
import ReceiveComments from "../Comment/ReceiveComments";
import { useCallback, useState } from "react";
import AsnButton from "../Asn/AsnButton";
import { DatePickerMobileActionBar } from "@/components/Common/DatePickerMobileActionBar";
import { InvoiceHeaderSummaryRows } from "./InvoiceSummaryRows";
export const InvoiceHeaderDetails = (props: InvoiceHeaderDetailsProps) => {
	const classes = useStyles();
	const {
		invoiceNumber,
		purchaseOrderNumber,
		purchaseOrderStatus,
		storeId,
		storeName,
		supplierName,
		invoiceType,
		receiveStatus,
		latestStatusHistory,
		invoiceDate,
		receivedDate,
		totalPriceInc,
		totalFreightInc,
		totalQty,
		failureReason,
		isExpandedHeader,
		onExpandedButtonClick,
		setReceivedDate,
		isDeliveredDateDisabled,
		comments,
		receiveId,
		asns,
	} = props;
	const handleReceivedDateChange = (date) => {
		if (date && !isNaN(date.getTime())) {
			setReceivedDate(date);
		}
	};

	const [asnDetailsModalOpen, setAsnDetailsModalOpen] = useState(false);

	const onAsnDetailsModalOpen = useCallback(() => {
		setAsnDetailsModalOpen(true);
	}, []);

	const onAsnDetailsModalClose = useCallback(() => {
		setAsnDetailsModalOpen(false);
	}, []);

	// Parse receivedDate - it could be a string (from API), Date object, or null
	// Treat 0001-01-01 (DateTime.MinValue from .NET) as null so the calendar defaults to today
	const isMinDate = (date: Date) => date.getFullYear() <= 1;
	const parsedReceivedDate = (() => {
		if (!receivedDate) return null;
		if (receivedDate instanceof Date) return isMinDate(receivedDate) ? null : receivedDate;
		if (typeof receivedDate === "string") {
			const parsed = parseISO(receivedDate);
			if (isNaN(parsed.getTime()) || isMinDate(parsed)) return null;
			return parsed;
		}
		return null;
	})();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	return (
		<>
			<Accordion expanded={isExpandedHeader} onChange={(e, expanded) => onExpandedButtonClick(expanded)}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="invoiceheaderdetails-content"
					id="invoicerheaderdetails"
					className={classes.invoiceCollapsePanelSummary}
				>
					<Grid container alignItems="center" sx={{ width: "100%" }}>
						<Grid container alignItems="center" size="auto">
							<Typography variant="body2" color="textSecondary">
								PO No
								{purchaseOrderNumber && (
									<>
										<Typography component="span" variant="body2" display="inline" color="textPrimary">
											{" " + purchaseOrderNumber}
										</Typography>
										<CopyToClipboard text={purchaseOrderNumber} />
									</>
								)}
							</Typography>
						</Grid>
						<Grid container justifyContent="flex-end" size="grow">
							<Grid>
								<StatusLabel status={receiveStatus || ""} size={isMobile ? "small" : "medium"} />
							</Grid>
						</Grid>
					</Grid>
				</AccordionSummary>
				<AccordionDetails>
					<Grid container>
						<Grid container size={8} spacing={1}>
							<Grid size={12}>
								<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
									PO Status
								</Typography>
								<Typography className={classes.value} variant="body2">
									{purchaseOrderStatus}
								</Typography>
							</Grid>
						</Grid>
						<Grid container size={4} spacing={1}>
							<Grid size={12}>
								<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
									Invoice Type
								</Typography>
								<Typography className={classes.value} variant="body2">
									{invoiceType}
								</Typography>
							</Grid>
						</Grid>
						<Grid container size={8} spacing={1}>
							<Grid size={12}>
								<Typography className={classes.caption} variant="caption" color="textSecondary">
									Invoice No
								</Typography>
								{invoiceNumber && (
									<Box display="flex" alignItems="center">
										<Typography className={classes.value} variant="body2">
											{invoiceNumber}
										</Typography>
										<CopyToClipboard text={invoiceNumber} />
									</Box>
								)}
							</Grid>
						</Grid>
						<Grid container size={4} spacing={1}>
							<Grid size={12}>
								<Typography className={classes.caption} variant="caption" color="textSecondary" display="inline">
									Total Cost Inc
								</Typography>
								<Typography className={classes.value} variant="body2">
									{`$${(totalPriceInc + totalFreightInc)?.toFixed(2)}`}
								</Typography>
							</Grid>
						</Grid>
						<InvoiceHeaderSummaryRows
							storeName={`${storeId} - ${storeName}`}
							supplierName={supplierName}
							totalPriceInc={totalPriceInc}
							totalFreightInc={totalFreightInc}
							totalQty={totalQty}
							invoiceDate={invoiceDate}
						/>
						<Grid container size={8}>
							<Grid size={12}>
								<Box display="flex" flexDirection="row">
									<Box>
										<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
											<DatePicker
												label="Delivered Date"
												value={parsedReceivedDate}
												onChange={handleReceivedDateChange}
												maxDate={new Date()}
												closeOnSelect
												disabled={isDeliveredDateDisabled}
												slots={{ actionBar: DatePickerMobileActionBar }}
												slotProps={{
													textField: {
														id: "received-date",
														size: "small",
														variant: "standard",
													},
													field: {
														InputProps: {
															endAdornment: (
																<InputAdornment position="end">
																	<CalendarIcon fontSize="small" />
																</InputAdornment>
															),
														},
													},
													actionBar: {
														actions: isMobile ? ["cancel", "accept"] : [],
														onSetDefaultDate: () => {
															if (!parsedReceivedDate) setReceivedDate(new Date());
														},
													} as any,
												}}
											/>
										</LocalizationProvider>
									</Box>
								</Box>
							</Grid>
						</Grid>

						<Grid container size={8}>
							<Grid size={12}>
								<ReceiveComments initialComments={comments ?? []} receiveId={receiveId} />
							</Grid>
						</Grid>
						{asns.length > 0 && (
							<Grid container size={4}>
								<Grid size={12}>
									<AsnButton
										isOpen={asnDetailsModalOpen}
										asns={asns}
										fromLocationName={supplierName}
										toLocationId={storeId}
										toLocationName={storeName}
										onAsnDetailsModalOpen={onAsnDetailsModalOpen}
										onAsnDetailsModalClose={onAsnDetailsModalClose}
									/>
								</Grid>
							</Grid>
						)}
						{failureReason && (
							<Grid container size={12} className={classes.failureReasonGrid}>
								<ReceiveFailureAlert
									status={receiveStatus}
									failureReason={failureReason}
									latestStatusHistory={latestStatusHistory}
								/>
							</Grid>
						)}
					</Grid>
				</AccordionDetails>
			</Accordion>
		</>
	);
};
